import React from 'react';
import { experience } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Briefcase, Calendar, CheckCircle, Code2, Activity } from 'lucide-react';

const Experience = () => {
  return (
    <section id="experience" className="py-20 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            Professional <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full"></div>
        </div>

        {/* Experience Cards */}
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Current Position */}
          <Card className="bg-[#1a1a1b] border-gray-800 hover:border-blue-500/50 transition-all duration-300 overflow-hidden">
            <CardHeader className="border-b border-gray-800 bg-gradient-to-r from-blue-950/20 to-cyan-950/20">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center flex-shrink-0">
                  <Briefcase size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl text-white mb-2">
                    {experience.current.title}
                  </CardTitle>
                  <p className="text-lg text-blue-400 font-semibold mb-2">
                    {experience.current.organization}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar size={16} />
                    <span>{experience.current.period}</span>
                    <Badge className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-none ml-2">
                      Current
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-6">
              <p className="text-gray-300 leading-relaxed">
                {experience.current.description}
              </p>

              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle size={20} className="text-cyan-400" />
                  Key Responsibilities & Activities
                </h3>
                <ul className="space-y-3">
                  {experience.current.responsibilities.map((responsibility, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 mt-2 flex-shrink-0"></div>
                      <span>{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* National Medical System Project */}
          <Card className="bg-[#1a1a1b] border-gray-800 hover:border-cyan-500/50 transition-all duration-300 overflow-hidden">
            <CardHeader className="border-b border-gray-800 bg-gradient-to-r from-cyan-950/20 to-blue-950/20">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Activity size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl text-white mb-2">
                    {experience.nationalMedicalSystem.title}
                  </CardTitle>
                  <p className="text-lg text-cyan-400 font-semibold mb-2">
                    {experience.nationalMedicalSystem.role}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar size={16} />
                    <span>{experience.nationalMedicalSystem.period}</span>
                    <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-none ml-2">
                      Active Project
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-6">
              <p className="text-gray-300 leading-relaxed">
                {experience.nationalMedicalSystem.description}
              </p>

              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Code2 size={20} className="text-blue-400" />
                  Technical Contributions
                </h3>
                <ul className="space-y-3">
                  {experience.nationalMedicalSystem.contributions.map((contribution, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <CheckCircle size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>{contribution}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-[#0a0a0b] rounded-lg border border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Activity size={18} className="text-cyan-400" />
                  Project Impact
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {experience.nationalMedicalSystem.impact}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Experience;
